import React, { useState } from 'react';
import { Star, ThumbsUp, CheckCircle } from 'lucide-react';

const ReviewList = ({ reviews = [], averageRating = 0, distribution = {}, formatDate }) => {
  const [sortBy, setSortBy] = useState('newest');

  // =====================================================
  // FALLBACK DATE FORMATTER (if not passed as prop)
  // =====================================================
  const defaultFormatDate = (dateString) => {
    if (!dateString) return 'Date not available';
    
    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) return 'Invalid date';
      
      return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      });
    } catch (error) {
      return 'Date error';
    }
  };

  // Use passed formatDate or fallback
  const dateFormatter = formatDate || defaultFormatDate;

  // Safe array check
  const reviewsArray = Array.isArray(reviews) ? reviews : [];
  
  // Sort reviews
  const sortedReviews = [...reviewsArray].sort((a, b) => {
    switch (sortBy) {
      case 'highest':
        return b.rating - a.rating;
      case 'lowest':
        return a.rating - b.rating;
      case 'helpful':
        return (b.helpful_count || 0) - (a.helpful_count || 0);
      case 'newest':
      default:
        return new Date(b.created_at) - new Date(a.created_at);
    }
  });

  // Render star rating
  const renderStars = (rating) => {
    return (
      <div className="flex items-center gap-1">
        {[1, 2, 3, 4, 5].map((star) => (
          <Star
            key={star}
            className={`w-4 h-4 ${
              star <= rating
                ? 'fill-yellow-400 text-yellow-400'
                : 'text-gray-300'
            }`}
          />
        ))}
      </div>
    );
  };

  // Calculate rating percentages
  const getRatingPercentage = (count, total) => {
    return total > 0 ? Math.round((count / total) * 100) : 0;
  };

  const totalReviews = reviewsArray.length;

  return (
    <div className="bg-white rounded-2xl shadow-lg p-8">
      
      {/* Rating Summary */}
      <div className="mb-8 pb-8 border-b">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
          
          {/* Average Rating */}
          <div>
            <div className="flex items-center gap-4 mb-4">
              <div className="text-5xl font-bold text-dark-900">
                {parseFloat(averageRating).toFixed(1)}
              </div>
              <div>
                {renderStars(Math.round(averageRating))}
                <p className="text-sm text-gray-600 mt-1">
                  Based on {totalReviews} {totalReviews === 1 ? 'review' : 'reviews'}
                </p>
              </div>
            </div>
          </div>

          {/* Rating Distribution */}
          <div className="space-y-2">
            {[5, 4, 3, 2, 1].map((rating) => {
              const count = distribution[rating] || 0;
              const percentage = getRatingPercentage(count, totalReviews);
              
              return (
                <div key={rating} className="flex items-center gap-3">
                  <span className="text-sm font-medium text-dark-700 w-12">
                    {rating} star
                  </span>
                  <div className="flex-1 h-2 bg-gray-200 rounded-full overflow-hidden">
                    <div
                      className="h-full bg-yellow-400 transition-all duration-300"
                      style={{ width: `${percentage}%` }}
                    />
                  </div>
                  <span className="text-sm text-gray-600 w-12 text-right">
                    {percentage}%
                  </span>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* Sort Controls */}
      {reviewsArray.length > 0 && (
        <div className="mb-6 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
          <h3 className="text-xl font-bold text-dark-900">
            All Reviews ({sortedReviews.length})
          </h3>
          <select
            value={sortBy}
            onChange={(e) => setSortBy(e.target.value)}
            className="px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 bg-white"
          >
            <option value="newest">Newest First</option>
            <option value="highest">Highest Rating</option>
            <option value="lowest">Lowest Rating</option>
            <option value="helpful">Most Helpful</option>
          </select>
        </div>
      )}

      {/* Reviews List */}
      <div className="space-y-6">
        {sortedReviews.length > 0 ? (
          sortedReviews.map((review) => (
            <div
              key={review.id}
              className="pb-6 border-b border-gray-200 last:border-b-0"
            >
              {/* Review Header */}
              <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3 mb-3">
                <div className="flex-1">
                  <div className="flex items-center gap-3 mb-2">
                    <h4 className="font-semibold text-dark-900">
                      {review.name}
                    </h4>
                    {review.verified_purchase && (
                      <span className="flex items-center gap-1 px-2 py-1 bg-green-50 text-green-700 text-xs font-medium rounded-full">
                        <CheckCircle className="w-3 h-3" />
                        Verified Purchase
                      </span>
                    )}
                  </div>
                  <div className="flex items-center gap-2">
                    {renderStars(review.rating)}
                  </div>
                </div>
                
                {/* ================================================ */}
                {/* DATE DISPLAY - USING FORMATTER */}
                {/* ================================================ */}
                <div className="text-sm text-gray-500">
                  {dateFormatter(review.created_at)}
                </div>
              </div>

              {/* Review Title */}
              <h5 className="font-semibold text-dark-900 mb-2">
                {review.title}
              </h5>

              {/* Review Comment */}
              <p className="text-gray-700 mb-4 leading-relaxed">
                {review.comment}
              </p>

              {/* Helpful Button */}
              <div className="flex items-center gap-4">
                <button
                  className="flex items-center gap-2 text-sm text-gray-600 hover:text-primary-600 transition-colors"
                  onClick={() => {
                    // TODO: Implement helpful functionality
                    console.log('Mark helpful:', review.id);
                  }}
                >
                  <ThumbsUp className="w-4 h-4" />
                  Helpful ({review.helpful_count || 0})
                </button>
              </div>
            </div>
          ))
        ) : (
          <div className="text-center py-12">
            <div className="text-6xl mb-4">⭐</div>
            <p className="text-gray-500 text-lg mb-2">
              No reviews yet
            </p>
            <p className="text-gray-400">
              Be the first to review this product!
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default ReviewList;